/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { observer } from 'mobx-react';
import React from 'react';
import { Button, Dialog, Grid, IconButton, Typography, GridLegacy } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import CancelIcon from '@mui/icons-material/Cancel';
import { useViewModel } from '../../../../../hooks/useViewModel';
import { ModuleDetailViewModel } from './ModuleDetailViewModel';
import { ModuleDetailDataView } from './DataView/ModuleDetailDataView';
import { Image } from '../../../../../types/elements/Image';
import { ModuleDetailViewUseStyles } from './ModuleDetailViewStyles';

interface Props {
	open: boolean;
	moduleName: string;
	moduleDescription: string;
	moduleImage?: Image;
	closeDialog: () => void;
	removeFromCourse: () => void;
	hasCourseStarted: boolean;
	canCourseBeEdited: boolean;
}

export const ModuleDetailView = observer((props: Props) => {
	const viewModel = useViewModel(ModuleDetailViewModel);
	const { classes } = ModuleDetailViewUseStyles();
	const { open, moduleName, moduleDescription, moduleImage, closeDialog, removeFromCourse, canCourseBeEdited } = props;

	return (
		<>
			<Dialog open={open} fullWidth={true} onClose={closeDialog} classes={{ paper: classes.paper }}>
				<GridLegacy container className={classes.header}>
					<GridLegacy item xs={4} className={classes.titleContainer}>
						<Typography variant='h1' className={classes.title}>
							<FormattedMessage id={'modules.module_detail.preview'} />
						</Typography>
					</GridLegacy>
					<GridLegacy item xs={8} className={classes.headerButtons}>
						{canCourseBeEdited && (
							<Button variant='outlined' size='small' onClick={removeFromCourse} className={classes.removeButton}>
								<FormattedMessage id={'modules.module_editing.remove_module'} />
							</Button>
						)}
						<IconButton onClick={() => closeDialog()} className={classes.cancelButton}>
							<CancelIcon />
						</IconButton>
					</GridLegacy>
				</GridLegacy>
				<GridLegacy container className={classes.contentContainer}>
					<GridLegacy item xs={6}>
						<ModuleDetailDataView
							moduleImage={moduleImage}
							moduleName={moduleName}
							moduleDescription={moduleDescription}
							setShowDetailImage={() => viewModel.setShowDetailImage(true)}
						/>
					</GridLegacy>
				</GridLegacy>
			</Dialog>
			{moduleImage && (
				<Dialog open={viewModel.getShowDetailImage()} onClose={() => viewModel.setShowDetailImage(false)} maxWidth='lg'>
					<img src={moduleImage.src} alt={moduleImage.alt} />
				</Dialog>
			)}
		</>
	);
});
